import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch, jsonHeaders } from "../api/api";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { colorForId, textColorForBgHsl } from "../utils/color";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  startOfDay,
  endOfDay,
  isSameDay,
  startOfMonth,
  differenceInCalendarDays,
} from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function CalendarPage() {
  const location = useLocation();
  const [events, setEvents] = useState([]); // [{ id, title, start: Date, end: Date, allDay }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [modalMounted, setModalMounted] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const calWrapRef = useRef(null);

  function positionModalNearDate(day) {
    try {
      const wrap = calWrapRef.current;
      if (!wrap) return;
      const monthView = wrap.querySelector(".rbc-month-view");
      if (!monthView) return;
      const rows = monthView.querySelectorAll(".rbc-month-row");
      if (!rows || !rows.length) return;
      const firstVisible = startOfWeek(startOfMonth(date));
      const offset = Math.max(0, differenceInCalendarDays(day, firstVisible));
      const rowIndex = Math.floor(offset / 7);
      const colIndex = offset % 7;
      const row = rows[rowIndex];
      if (!row) return;
      const bgCells = row.querySelectorAll(".rbc-day-bg");
      const cell = bgCells && bgCells[colIndex];
      if (!cell) return;
      const rect = cell.getBoundingClientRect();
      const margin = 8;
      const modalW = 500; // should match modalCard width
      const modalHEst = 320; // rough estimate; good enough for flipping
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Prefer left of the cell
      let leftPx = rect.left - modalW - margin;
      let useRightSide = false;
      if (leftPx < margin) {
        // Not enough space on left, try right side
        leftPx = rect.right + margin;
        useRightSide = true;
      }
      // Clamp if still overflowing to the right
      if (leftPx + modalW > vw - margin) {
        leftPx = Math.max(margin, vw - modalW - margin);
      }

      // Vertical: prefer aligning to top of cell; if overflow bottom, flip above
      let topPx = rect.top;
      if (topPx + modalHEst > vh - margin) {
        topPx = Math.max(margin, rect.bottom - modalHEst);
      }

      setPopoverPos({
        top: topPx + window.scrollY,
        left: leftPx + window.scrollX,
        side: useRightSide ? "right" : "left",
      });
    } catch {}
  }

  function toRbcEvent(ev) {
    return {
      id: ev.eventId,
      title: ev.title,
      start: new Date(ev.startTime),
      end: new Date(ev.endTime),
      allDay: !!ev.allDay,
      createdBy: ev.createdBy,
      createdByName: ev.createdByName,
    };
  }

  async function loadEvents() {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const data = await apiFetch("/calendar", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const mapped = (Array.isArray(data) ? data : []).map(toRbcEvent);
      setEvents(mapped);
    } catch (err) {
      setError(err.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open add modal if navigated with a specific day to add
  useEffect(() => {
    try {
      const dayStr = location.state && location.state.addDay;
      if (dayStr) {
        const [y, m, d] = dayStr.split("-").map(Number);
        const s = new Date(y, (m || 1) - 1, d || 1, 9, 0, 0, 0);
        const e = new Date(s.getTime() + 60 * 60 * 1000);
        setAllDay(false);
        setStartTime(toLocalInput(s));
        setEndTime(toLocalInput(e));
        setView("week");
        setDate(s);
        setShowForm(true);
        setModalMounted(true);
        requestAnimationFrame(() => setModalVisible(true));
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!title || !startTime || !endTime) return;
    try {
      const token = localStorage.getItem("token");
      if (isEditing && editingEventId) {
        const updated = await apiFetch(`/calendar/${editingEventId}`, {
          method: "PATCH",
          headers: jsonHeaders({ Authorization: `Bearer ${token}` }),
          body: JSON.stringify({ title, startTime, endTime, allDay }),
        });
        const updatedEvent = updated?.event || updated;
        setEvents((prev) =>
          prev.map((ev) =>
            ev.id === editingEventId ? toRbcEvent(updatedEvent) : ev
          )
        );
      } else {
        const created = await apiFetch("/calendar", {
          method: "POST",
          headers: jsonHeaders({ Authorization: `Bearer ${token}` }),
          body: JSON.stringify({ title, startTime, endTime, allDay }),
        });
        // Optimistically add the new event to the calendar
        setEvents((prev) => [toRbcEvent(created), ...prev]);
      }
      // reset form
      setTitle("");
      setStartTime("");
      setEndTime("");
      setAllDay(false);
      setIsEditing(false);
      setEditingEventId(null);
      setShowForm(false);
      setModalVisible(false);
      setTimeout(() => {
        setModalMounted(false);
        setPopoverPos(null);
      }, 220);
    } catch (err) {
      setError(
        err.message ||
          (isEditing ? "Failed to update event" : "Failed to add event")
      );
    }
  }

  // delete handled via edit modal

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          School Calendar
        </h1>
        <div style={{ marginLeft: "auto" }}>
          {!showForm ? (
            <button
              style={btnPrimary}
              onClick={() => {
                if (!startTime && !endTime) {
                  const now = new Date();
                  const in1hr = new Date(now.getTime() + 60 * 60 * 1000);
                  setStartTime(toLocalInput(now));
                  setEndTime(toLocalInput(in1hr));
                }
                setShowForm(true);
                setModalMounted(true);
                requestAnimationFrame(() => setModalVisible(true));
              }}
            >
              + Add Event
            </button>
          ) : (
            <button
              style={btnSecondary}
              onClick={() => {
                setShowForm(false);
                setModalVisible(false);
                setTimeout(() => {
                  setModalMounted(false);
                  setPopoverPos(null);
                }, 220);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div
        ref={calWrapRef}
        style={{
          height: "70vh",
          marginBottom: 16,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          padding: 12,
        }}
      >
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={["month", "week", "day", "agenda"]}
          style={{ height: "100%" }}
          popup
          eventPropGetter={(event) => {
            const who = event.createdBy || event.createdByName || "";
            if (!who) return {};
            const bg = colorForId(who, { s: 65, l: 48 });
            const color = textColorForBgHsl(bg);
            return { style: { backgroundColor: bg, color } };
          }}
          view={view}
          onView={(v) => {
            setView(v);
            setPopoverPos(null);
          }}
          date={date}
          onNavigate={(d) => {
            setDate(d);
            setPopoverPos(null);
          }}
          dayPropGetter={(d) => {
            if (view !== "month" || !selectedDay) return {};
            if (isSameDay(d, selectedDay)) {
              return {
                style: {
                  background: "rgba(45,108,223,0.08)",
                  outline: "2px solid #2d6cdf",
                  outlineOffset: "-2px",
                  borderRadius: 6,
                },
              };
            }
            return {};
          }}
          selectable
          onSelectSlot={({ start, end, action }) => {
            try {
              if (view === "month") {
                const s = startOfDay(start);
                const e = endOfDay(start);
                setSelectedDay(s);
                positionModalNearDate(s);
                setAllDay(true);
                setStartTime(toLocalInput(s));
                setEndTime(toLocalInput(e));
              } else {
                setAllDay(false);
                const s = toLocalInput(start);
                // Force 1-hour duration
                setStartTime(s);
                setEndTime(shiftLocalMinutes(s, 60));
              }
              setIsEditing(false);
              setEditingEventId(null);
              setShowForm(true);
              setModalMounted(true);
              requestAnimationFrame(() => setModalVisible(true));
              if (!title) setTitle("");
            } catch {}
          }}
          onSelectEvent={(ev) => {
            try {
              // Open edit modal for clicked event
              setIsEditing(true);
              setEditingEventId(ev.id);
              setTitle(ev.title || "");
              setAllDay(!!ev.allDay);
              setStartTime(toLocalInput(ev.start));
              setEndTime(toLocalInput(ev.end));
              if (view === "month") {
                const s = startOfDay(ev.start);
                setSelectedDay(s);
                positionModalNearDate(s);
              } else {
                setPopoverPos(null);
              }
              setShowForm(true);
              setModalMounted(true);
              requestAnimationFrame(() => setModalVisible(true));
            } catch {}
          }}
        />
      </div>
      {modalMounted && (
        <div
          style={{
            ...modalOverlay,
            opacity: modalVisible ? 1 : 0,
            transition: "opacity 220ms ease",
          }}
          onClick={() => {
            setShowForm(false);
            setModalVisible(false);
            setTimeout(() => {
              setModalMounted(false);
              setPopoverPos(null);
            }, 220);
          }}
        >
          <div
            style={{
              ...modalCard,
              ...(popoverPos
                ? {
                    position: "absolute",
                    top: popoverPos.top,
                    left: popoverPos.left,
                  }
                : {}),
              opacity: modalVisible ? 1 : 0,
              transform: modalVisible
                ? "translateY(0) scale(1)"
                : "translateY(-6px) scale(0.98)",
              transition: "opacity 220ms ease, transform 220ms ease",
              position: popoverPos ? "absolute" : "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeader}>
              <div style={{ fontWeight: 700 }}>
                {isEditing ? "Edit Event" : "New Event"}
              </div>
              <button
                aria-label="Close"
                onClick={() => {
                  setShowForm(false);
                  setModalVisible(false);
                  setTimeout(() => {
                    setModalMounted(false);
                    setPopoverPos(null);
                  }, 220);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAdd}>
              {error && (
                <div style={errBox}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              <div style={{ ...modalBody, display: "grid", gap: 10 }}>
                <input
                  style={{
                    ...inputStyle,
                    maxWidth: 340,
                    margin: "0 auto",
                    display: "block",
                  }}
                  placeholder="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
                <div
                  style={{ display: "grid", gap: 16, justifyItems: "center" }}
                >
                  <label
                    style={{ ...labelStyle, width: "100%", maxWidth: 360 }}
                  >
                    Start
                    <div style={tripleRow}>
                      <select
                        aria-label="Start month"
                        style={inputStyleSm}
                        value={parseParts(startTime).monthIndex}
                        onChange={(e) => {
                          const parts = parseParts(startTime);
                          const m = Number(e.target.value);
                          parts.monthIndex = m;
                          parts.day = clampDay(parts.year, m, parts.day);
                          const next = partsToLocalInput(parts);
                          setStartTime(next);
                          if (!allDay) setEndTime(shiftLocalMinutes(next, 60));
                        }}
                      >
                        {MONTHS.map((m, i) => (
                          <option key={m} value={i}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label="Start day"
                        style={inputStyleXs}
                        type="number"
                        min={1}
                        max={31}
                        value={parseParts(startTime).day}
                        onChange={(e) => {
                          const parts = parseParts(startTime);
                          let d = Number(e.target.value || 1);
                          d = clampDay(parts.year, parts.monthIndex, d);
                          parts.day = d;
                          const next = partsToLocalInput(parts);
                          setStartTime(next);
                          if (!allDay) setEndTime(shiftLocalMinutes(next, 60));
                        }}
                      />
                      {!allDay && (
                        <input
                          aria-label="Start time"
                          style={inputStyleTime}
                          type="time"
                          step={60}
                          value={toTimeString(parseParts(startTime))}
                          onChange={(e) => {
                            const parts = parseParts(startTime);
                            const [hh, mm] = e.target.value
                              .split(":")
                              .map(Number);
                            parts.hours = isNaN(hh) ? 0 : hh;
                            parts.minutes = isNaN(mm) ? 0 : mm;
                            const next = partsToLocalInput(parts);
                            setStartTime(next);
                            setEndTime(shiftLocalMinutes(next, 60));
                          }}
                        />
                      )}
                    </div>
                    <span style={hintText}>
                      {formatWords(startTime, allDay)}
                    </span>
                  </label>
                  <label
                    style={{ ...labelStyle, width: "100%", maxWidth: 360 }}
                  >
                    End
                    <div style={tripleRow}>
                      <select
                        aria-label="End month"
                        style={inputStyleSm}
                        value={parseParts(endTime).monthIndex}
                        onChange={(e) => {
                          const parts = parseParts(endTime);
                          const m = Number(e.target.value);
                          parts.monthIndex = m;
                          parts.day = clampDay(parts.year, m, parts.day);
                          const next = partsToLocalInput(parts);
                          setEndTime(next);
                          if (!allDay)
                            setStartTime(shiftLocalMinutes(next, -60));
                        }}
                      >
                        {MONTHS.map((m, i) => (
                          <option key={m} value={i}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label="End day"
                        style={inputStyleXs}
                        type="number"
                        min={1}
                        max={31}
                        value={parseParts(endTime).day}
                        onChange={(e) => {
                          const parts = parseParts(endTime);
                          let d = Number(e.target.value || 1);
                          d = clampDay(parts.year, parts.monthIndex, d);
                          parts.day = d;
                          const next = partsToLocalInput(parts);
                          setEndTime(next);
                          if (!allDay)
                            setStartTime(shiftLocalMinutes(next, -60));
                        }}
                      />
                      {!allDay && (
                        <input
                          aria-label="End time"
                          style={inputStyleTime}
                          type="time"
                          step={60}
                          value={toTimeString(parseParts(endTime))}
                          onChange={(e) => {
                            const parts = parseParts(endTime);
                            const [hh, mm] = e.target.value
                              .split(":")
                              .map(Number);
                            parts.hours = isNaN(hh) ? 0 : hh;
                            parts.minutes = isNaN(mm) ? 0 : mm;
                            const next = partsToLocalInput(parts);
                            setEndTime(next);
                            setStartTime(shiftLocalMinutes(next, -60));
                          }}
                        />
                      )}
                    </div>
                    <span style={hintText}>{formatWords(endTime, allDay)}</span>
                  </label>
                </div>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={allDay}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAllDay(checked);
                      if (!checked) {
                        const base = startTime || toLocalInput(new Date());
                        if (!startTime) setStartTime(base);
                        setEndTime(shiftLocalMinutes(base, 60));
                      }
                    }}
                  />
                  All day
                </label>
                <div style={summaryBox}>
                  <strong>When:</strong>&nbsp;
                  {title ? `${title} — ` : ""}
                  {formatWords(startTime, allDay)}
                  {startTime && endTime && !allDay
                    ? ` to ${formatWords(endTime, allDay)}`
                    : ""}
                </div>
              </div>
              <div style={modalFooter}>
                {isEditing && (
                  <button
                    type="button"
                    style={{
                      ...btnSecondary,
                      marginRight: "auto",
                      color: "#b91c1c",
                      borderColor: "#fecaca",
                      background: "#fee2e2",
                    }}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  style={btnSecondary}
                  onClick={() => {
                    setShowForm(false);
                    setIsEditing(false);
                    setEditingEventId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...btnPrimary,
                    opacity: title && startTime && endTime ? 1 : 0.6,
                  }}
                  disabled={!title || !startTime || !endTime}
                >
                  {isEditing ? "Update" : "Save"}
                </button>
              </div>
              {showDeleteConfirm && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0,0,0,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 12,
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      padding: 16,
                      borderRadius: 10,
                      width: 320,
                      boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>
                      Delete event?
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#475569",
                        marginBottom: 12,
                      }}
                    >
                      This can’t be undone.
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                      }}
                    >
                      <button
                        type="button"
                        style={btnSecondary}
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        style={{
                          ...btnSecondary,
                          color: "#b91c1c",
                          borderColor: "#fecaca",
                          background: "#fee2e2",
                        }}
                        onClick={async () => {
                          if (!editingEventId) return;
                          try {
                            const token = localStorage.getItem("token");
                            await apiFetch(`/calendar/${editingEventId}`, {
                              method: "DELETE",
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            setEvents((prev) =>
                              prev.filter((e) => e.id !== editingEventId)
                            );
                            setShowDeleteConfirm(false);
                            setShowForm(false);
                            setModalVisible(false);
                            setTimeout(() => {
                              setModalMounted(false);
                              setPopoverPos(null);
                            }, 220);
                            setIsEditing(false);
                            setEditingEventId(null);
                          } catch (err) {
                            setError(err.message || "Failed to delete event");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {loading && <p>Loading…</p>}
    </div>
  );
}

function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const h = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${y}-${m}-${d}T${h}:${min}`;
}

// Friendly text like "October 17th, 25" or with time "October 17th, 25 3:00 PM"
function formatWords(dateString, allDay) {
  if (!dateString) return "—";
  const d = new Date(dateString);
  try {
    const base = format(d, "MMMM do, yy");
    if (allDay) return base;
    return `${base} ${format(d, "h:mm a")}`;
  } catch {
    return dateString;
  }
}

// --- Custom Month/Day/Time helpers ---
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function clampDay(year, monthIndex, day) {
  const max = daysInMonth(year, monthIndex);
  if (day < 1) return 1;
  if (day > max) return max;
  return day;
}

function parseParts(localIso) {
  // expects "YYYY-MM-DDTHH:mm"; if missing or invalid, fallback to now
  const now = new Date();
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})$/.exec(
    localIso || ""
  );
  if (!m) {
    return {
      year: now.getFullYear(),
      monthIndex: now.getMonth(),
      day: now.getDate(),
      hours: now.getHours(),
      minutes: now.getMinutes(),
    };
  }
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  const hours = Number(m[4]);
  const minutes = Number(m[5]);
  return { year, monthIndex, day, hours, minutes };
}

function partsToLocalInput(parts) {
  const pad = (n) => String(n).padStart(2, "0");
  const y = parts.year;
  const m = pad((parts.monthIndex ?? 0) + 1);
  const d = pad(parts.day ?? 1);
  const h = pad(parts.hours ?? 0);
  const min = pad(parts.minutes ?? 0);
  return `${y}-${m}-${d}T${h}:${min}`;
}

function toTimeString(parts) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(parts.hours ?? 0)}:${pad(parts.minutes ?? 0)}`;
}

// Shift a local datetime string (YYYY-MM-DDTHH:mm) by N minutes and return the same format
function shiftLocalMinutes(localIso, minutes) {
  try {
    const p = parseParts(localIso);
    const d = new Date(p.year, p.monthIndex, p.day, p.hours, p.minutes, 0, 0);
    const shifted = new Date(d.getTime() + minutes * 60 * 1000);
    return toLocalInput(shifted);
  } catch {
    return localIso;
  }
}

const inputStyle = {
  width: "90%",
  padding: "8px 10px",
  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: 14,
  lineHeight: 1.2,
};

const inputStyleSm = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: 14,
  height: 50,
  lineHeight: 1.2,
  boxSizing: "border-box",
};

const inputStyleXs = {
  width: 56,
  padding: "6px 8px",
  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: 14,
  height: 36,
};

// Time input: match heights, add subtle left margin so it clears the day box visually
const inputStyleTime = {
  width: "100%",
  padding: "6px 8px",

  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: 14,
  height: 36,
  marginLeft: 20,
};

const labelStyle = { display: "grid", gap: 4, fontSize: 12, color: "#333" };

const btnPrimary = {
  background: "#2d6cdf",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

const btnSecondary = {
  background: "#f0f2f5",
  color: "#333",
  border: "1px solid #dcdfe4",
  borderRadius: 6,
  padding: "10px 14px",
  fontWeight: 600,
  cursor: "pointer",
};

const errBox = {
  background: "#fee",
  color: "#a00",
  border: "1px solid #fbb",
  padding: 8,
  borderRadius: 6,
  margin: "8px 0",
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalCard = {
  width: "min(500px, 92vw)",
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  padding: 12,
};

const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 8,
};

const modalFooter = {
  marginTop: 14,
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
};

const modalBody = {
  maxWidth: 380,
  margin: "0 auto",
};

const tripleRow = {
  display: "grid",
  gridTemplateColumns: "minmax(120px, 1fr) 56px minmax(140px, 1.2fr)",
  gap: 12,
  alignItems: "center",
  marginTop: 6,
  marginBottom: 6,
};

const hintText = {
  fontSize: 11,
  color: "#6b7280",
  marginTop: 6,
};

const summaryBox = {
  background: "#f8fafc",
  border: "1px solid #eef1f6",
  color: "#334155",
  padding: 6,
  borderRadius: 8,
  fontSize: 13,
  marginTop: 4,
};
